import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAIAccuracy } from '@/hooks/useDashboard'

interface Props {
  days: number
}

function fmtPct(v: number) {
  return `${v.toFixed(1)}%`
}

function fmtConf(v: number) {
  // Confidence is 0-1 in the DB.
  if (v <= 1) return `${(v * 100).toFixed(1)}%`
  return v.toFixed(1)
}

export function AIAccuracyCard({ days }: Props) {
  const { data, isLoading, isError } = useAIAccuracy(days)

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">AI accuracy</CardTitle>
        <p className="text-xs text-neutral-500">
          Last {days} days
          {data && data.sample_size > 0 ? ` · ${data.sample_size} finalized invoices` : ''}
        </p>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="h-32 animate-pulse rounded bg-neutral-100" />}
        {isError && <div className="text-sm text-red-600">Could not load AI stats.</div>}
        {data && !isLoading && !isError && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Stat label="Auto-approved" value={fmtPct(data.auto_approved_pct)} />
              <Stat label="Avg confidence" value={fmtConf(data.avg_confidence)} />
            </div>
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">
                Most-corrected fields
              </div>
              {data.top_corrected_fields.length === 0 ? (
                <div className="text-sm text-neutral-500">No corrections in this window.</div>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.top_corrected_fields.map((f) => (
                    <li
                      key={f.field}
                      className="flex items-center justify-between text-neutral-700"
                    >
                      <span className="truncate">{f.field}</span>
                      <span className="ml-2 shrink-0 rounded bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700">
                        {f.count}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="mt-0.5 text-2xl font-semibold text-neutral-950">{value}</div>
    </div>
  )
}
